import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import Navbar from "../Home/Components/Navbar/Navbar";
import Footer from "../../GlobalComp/Footer/Footer";
import { HERO_SRC, HERO_SRCSET, HERO_SIZES } from "../../../utils/heroImage";
import Classes from "../ServicePages/ServicePage.module.css";
import { SITE_URL, BUSINESS_NAME, BOOKING_URL, PHONE_DISPLAY, PHONE_TEL } from "../ServicePages/serviceData";

// Messages go to api/contact.js, which emails them through Gmail. The form
// fetches a signed spam-protection token when the page loads and sends it back
// with the message.
const FORM_ENDPOINT = "/api/contact";

// The API treats messages sent within 3 seconds of getting a token as bots.
const MIN_TOKEN_AGE_MS = 3200;
const MAX_TOKEN_AGE_MS = 12 * 60 * 60 * 1000;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Formats US numbers as (561) 555-0100 while typing; 10 digits max.
function formatPhone(value) {
    let d = value.replace(/\D/g, "");
    if (d[0] === "1") d = d.slice(1);
    d = d.slice(0, 10);
    if (d.length === 0) return "";
    if (d.length <= 3) return `(${d}`;
    if (d.length <= 6) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
    return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

const isValidPhone = (value) => /^[2-9]\d{2}[2-9]\d{6}$/.test(value.replace(/\D/g, ""));

async function fetchToken() {
    const res = await fetch(FORM_ENDPOINT, { headers: { Accept: "application/json" } });
    if (!res.ok) throw new Error(`Token request failed: ${res.status}`);
    const { token } = await res.json();
    return { token, issuedAt: Date.now() };
}

const TITLE = `Contact Us | ${BUSINESS_NAME}`;
const DESCRIPTION =
    "Contact Quick Vents and Appliance Repair in Boca Raton, FL. Send us a message, call (561) 929-5476, or book online. Serving Palm Beach and Broward Counties.";
const URL = `${SITE_URL}/contact`;

const schema = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: TITLE,
    url: URL,
    about: { "@id": `${SITE_URL}/#business` },
};

const EMPTY = { name: "", phone: "", email: "", message: "", company: "" };

export default function ContactPage() {
    const [form, setForm] = useState(EMPTY);
    const [status, setStatus] = useState("idle"); // idle | sending | sent | error
    const [phoneTouched, setPhoneTouched] = useState(false);
    const tokenRef = useRef(null);
    const phoneRef = useRef(null);

    useEffect(() => {
        fetchToken()
            .then((t) => { tokenRef.current = t; })
            .catch(() => {});
    }, []);

    const phoneError = phoneTouched && !isValidPhone(form.phone);

    const onChange = (e) => {
        const { name, value } = e.target;
        setForm({ ...form, [name]: name === "phone" ? formatPhone(value) : value });
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!isValidPhone(form.phone)) {
            setPhoneTouched(true);
            phoneRef.current?.focus();
            return;
        }

        setStatus("sending");
        try {
            let t = tokenRef.current;
            if (!t || Date.now() - t.issuedAt > MAX_TOKEN_AGE_MS) t = tokenRef.current = await fetchToken();
            const age = Date.now() - t.issuedAt;
            if (age < MIN_TOKEN_AGE_MS) await wait(MIN_TOKEN_AGE_MS - age);

            const res = await fetch(FORM_ENDPOINT, {
                method: "POST",
                headers: { "Content-Type": "application/json", Accept: "application/json" },
                body: JSON.stringify({ ...form, token: t.token }),
            });
            if (!res.ok) throw new Error(`Request failed: ${res.status}`);
            setStatus("sent");
            setForm(EMPTY);
            setPhoneTouched(false);
        } catch (err) {
            tokenRef.current = null;
            setStatus("error");
        }
    };

    return (
        <div className={Classes.page}>
            <Helmet>
                <title>{TITLE}</title>
                <meta name="description" content={DESCRIPTION} />
                <link rel="canonical" href={URL} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={TITLE} />
                <meta property="og:description" content={DESCRIPTION} />
                <meta property="og:url" content={URL} />
                <script type="application/ld+json">{JSON.stringify(schema)}</script>
            </Helmet>

            <Navbar />

            <header className={Classes.hero}>
                <img src={HERO_SRC} srcSet={HERO_SRCSET} sizes={HERO_SIZES} alt="" className={Classes.heroBg} draggable={false} fetchpriority="high" decoding="async" />
                <div className={Classes.heroOverlay} />
                <div className={Classes.heroContent}>
                    <p className={Classes.eyebrow}>Contact</p>
                    <h1>Contact Quick Vents + Appliance Repair</h1>
                    <p className={Classes.intro}>
                        Send us a message and we'll get back to you, or call{" "}
                        <a href={PHONE_TEL} className={Classes.heroLink}>{PHONE_DISPLAY}</a>. Ready to schedule?{" "}
                        <a href={BOOKING_URL} target="_blank" rel="noreferrer" className={Classes.heroLink}>Book online</a>.
                    </p>
                </div>
            </header>

            <main className={Classes.formWrap}>
                {status === "sent" ? (
                    <div className={`${Classes.formCard} ${Classes.formSuccess}`} role="status">
                        <span className={Classes.successIcon} aria-hidden="true">
                            <svg viewBox="0 0 24 24">
                                <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                            </svg>
                        </span>
                        <h2>Thanks, we got your message!</h2>
                        <p className={Classes.formNote}>
                            We'll get back to you within 24–48 hours. Need us sooner? Call{" "}
                            <a href={PHONE_TEL}>{PHONE_DISPLAY}</a>.
                        </p>
                        <Link to="/" className={Classes.primaryBtn}>
                            Continue back to homepage
                        </Link>
                    </div>
                ) : (
                    <form className={Classes.formCard} onSubmit={onSubmit}>
                        <h2>Send us a message</h2>
                        {/* Hidden from people; bots that fill it in are ignored by api/contact.js */}
                        <input
                            type="text"
                            name="company"
                            value={form.company}
                            onChange={onChange}
                            tabIndex={-1}
                            autoComplete="off"
                            aria-hidden="true"
                            className={Classes.honeypot}
                        />
                        <div className={Classes.formRow}>
                            <label className={Classes.field}>
                                <span>Name</span>
                                <input name="name" value={form.name} onChange={onChange} required autoComplete="name" />
                            </label>
                            <label className={`${Classes.field} ${phoneError ? Classes.fieldInvalid : ""}`}>
                                <span>Phone</span>
                                <input
                                    ref={phoneRef}
                                    name="phone"
                                    type="tel"
                                    inputMode="tel"
                                    value={form.phone}
                                    onChange={onChange}
                                    onBlur={() => form.phone && setPhoneTouched(true)}
                                    required
                                    autoComplete="tel-national"
                                    maxLength={14}
                                    aria-invalid={phoneError}
                                    aria-describedby={phoneError ? "phone-error" : undefined}
                                />
                                {phoneError && (
                                    <em id="phone-error" className={Classes.fieldError}>
                                        Enter a valid 10-digit US phone number
                                    </em>
                                )}
                            </label>
                        </div>
                        <label className={Classes.field}>
                            <span>Email</span>
                            <input name="email" type="email" value={form.email} onChange={onChange} required autoComplete="email" />
                        </label>
                        <label className={Classes.field}>
                            <span>How can we help?</span>
                            <textarea
                                name="message"
                                rows={5}
                                value={form.message}
                                onChange={onChange}
                                required
                                placeholder="Dryer vent cleaning, appliance repair, or a question"
                            />
                        </label>
                        <button type="submit" className={Classes.primaryBtn} disabled={status === "sending"}>
                            {status === "sending" ? "Sending..." : "Send message"}
                        </button>
                    </form>
                )}
            </main>

            <Footer />
        </div>
    );
}
